import _ from 'underscore';
import { createOrUpdateCampaign } from '@bingads-webui-campaign-react/perf-max-data';
import { Campaign } from '@bingads-webui/campaign-odata-schemas';
import {
  viewModelToBulkModelForAdSchedules,
  viewModelToBulkModelForLocationTargets,
} from '@bingads-webui-campaign-react/perf-max-step-wizard';
import { ERROR_ENTITY, isBulkWrappedResponse, getBulkUploadErrorType, modelToViewModelForBulkErrors, removeReferencedEntityInvalidIdErrors } from '@bingads-webui-campaign-react/xandr-video-campaign-shared-components';
import { bulkModelToViewModelForCampaign, viewModelToBulkModelForCampaign } from './campaign';
import { GENERIC_ERROR_MESSAGE_KEY } from '../shared/constants';

const bulkUpsertCampaignSettingsEntityErrorMap = {
  [getBulkUploadErrorType(Campaign)]: ERROR_ENTITY.Campaign,
  'Campaign Location Criterion Error': ERROR_ENTITY.CampaignLocation,
  'Campaign Radius Criterion Error': ERROR_ENTITY.CampaignRadius,
  'Campaign Negative Location Criterion Error': ERROR_ENTITY.CampaignNegativeLocation,
  'Campaign Location Intent Criterion Error': ERROR_ENTITY.CampaignLocationIntent,
};

export const getErrorsPlaceholders = () => ({
  serverErrors: [],
  systemErrors: [],
});

export const saveCampaign = ({
  campaign: campaignViewModel,
  bundleLocationCriterions,
  storeId,
  appId,
  bundleCountries,
}, { scenario, i18n, activityLogger }) => {
  const campaignModel = viewModelToBulkModelForCampaign(campaignViewModel);
  const adSchedulesModel = viewModelToBulkModelForAdSchedules(campaignViewModel);

  if (!campaignModel.Id || campaignModel.Id < 0) {
    campaignModel.ProviderId = storeId;
    campaignModel.SalesCountry = 'US';
    campaignModel.Priority = 'High';
  }

  if (!campaignModel.CampaignSettings) {
    campaignModel.CampaignProductScope = {
      CampaignName: campaignModel.Name,
      ProductCondition1: 'MPN',
      ProductOperator1: 'equal',
      ProductValue1: appId,
    };
  }

  let locationTargetsModel = null;
  const hasLocationViewModel = !!campaignViewModel.data.locationTargets;
  if (hasLocationViewModel) {
    // if has lcoation view model then either its an existing campaign, or a new one a locaiton targeting is supported. Use whatever we have in view model.
    locationTargetsModel = hasLocationViewModel && viewModelToBulkModelForLocationTargets(campaignViewModel, bundleLocationCriterions);
  } else {
    // no location view model then this must be a new campaign and location targeting support is off => create model to default to US
    locationTargetsModel = _.find(bundleLocationCriterions, (criterion => criterion.Criterion.LocationCriterion.CountryCode === 'US'));
    locationTargetsModel = _.extend(locationTargetsModel, {
      Campaign: {
        Id: campaignModel.Id,
      },
    });
    locationTargetsModel = [locationTargetsModel];
  }

  const modelToSave = {
    ...campaignModel,
    BiddingScheme: {
      Type: campaignModel.BiddingScheme.Type,
      ManualCpi: campaignModel.BiddingScheme.ManualCPI,
    },
    CampaignCriterions: locationTargetsModel ? [
      ...adSchedulesModel,
      ...locationTargetsModel,
    ] : [...adSchedulesModel],
  };

  return createOrUpdateCampaign(modelToSave)
    // with bulk the result will have shape of:
    // - { ...entityData } - in case of success;
    // - { entity: { ...entityData }, errors: [(EditorialError|MTError)] } - in case of error(s).
    .then((result) => {
      const campaign = bulkModelToViewModelForCampaign({
        campaignModel: result,
        originalCampaignViewModel: campaignViewModel,
        bundleCountries,
      });

      const data = {
        campaign,
        ...getErrorsPlaceholders(),
      };

      return data;
    }, (bulkOrSystemError) => {
      if (isBulkWrappedResponse(bulkOrSystemError)) {
        const { entity, errors } = bulkOrSystemError;
        const campaign = entity ?
          bulkModelToViewModelForCampaign({
            campaignModel: entity,
            originalCampaignViewModel: campaignViewModel,
            errors,
            bundleCountries,
          }) :
          campaignViewModel;

        let filteredErrors = errors;
        if (campaign.data.Id <= 0) {
          filteredErrors = removeReferencedEntityInvalidIdErrors(errors);
        }
        const serverErrors = modelToViewModelForBulkErrors(
          filteredErrors,
          bulkUpsertCampaignSettingsEntityErrorMap,
          scenario,
          i18n,
          activityLogger
        );

        const data = {
          campaign,
          ...getErrorsPlaceholders(),
          serverErrors,
        };

        return _.isEmpty(filteredErrors) ? data : Promise.reject(data);
      }

      // eslint-disable-next-line prefer-promise-reject-errors
      return Promise.reject({
        campaign: campaignViewModel,
        systemErrors: [i18n.getString(GENERIC_ERROR_MESSAGE_KEY)],
        // TODO: We would want to return the criterions with Ids, but UI's bulk drops them
      });
    });
};
